import { Journey } from '@audibene/ta.utils-opportunity'
import { isBefore, isEqual } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIsFetching, useQuery } from 'react-query'
import { filterResource } from 'util/filterResources'
import { getSpecialistFirstAvailability } from 'util/getSpecialistFirstAvailability'
import {
  ANY_RESOURCE_ID,
  ANY_INTERNAL_RESOURCE_ID,
  ANY_EXTERNAL_RESOURCE_ID,
  AppointmentType,
  AvailabilityMode,
  Country,
} from '../../constants'
import { filterByExternalResource, filterByInternalResource } from 'util/filterByResource'
import { AppointmentFormData, AvailabilitiesResponse, DayAvailability, FirstAvailability, Resource } from '../../types'
import { logFactory, mergeAvailabilities } from '../../util'
import { ErrorCode } from '../../util/errorCodeToDisplayText/errorCode'
import filterSlotsByResourceTypes from '../../util/filterSlotsByResourceTypes'
import { useAppointmentsService } from '../services'
import { useAvailabilitiesRetry } from '../useAvailabilitiesRetry'
import { useDateFormat } from '../useDateFormat'
import { useAvailabilityCacheEnabled } from '../useAvailabilityCacheEnabled'
import { useFirstAvailability } from '../useFirstAvailability'
import { SchedulingStatus, useSchedulingState } from '../useSchedulingState'
import { appendAllResourcesDayAvailability } from './appendAllResourcesDayAvailability'
import { mergeDayAvailability } from './mergeDayAvailability'

interface Props {
  appointmentType: AppointmentType
  country: Country
  dateEnd: Date
  dateStart: Date
  durationInMinutes: number
  isCabinOnlyConfiguration?: boolean
  isEnabled?: boolean
  journey: Journey
  paramsSettled?: boolean
  resourceIds: string[]
  resources: Resource[]
  timeZone: string
  mode?: AvailabilityMode
}

type GetAvailabilitiesParameters = {
  appointment_type: AppointmentType
  customer_timezone: string
  duration_in_minutes?: number
  date_end: Date
  date_start: Date
  is_verbose?: boolean
  journey: Journey
  resource_gids: string[]
  mode?: AvailabilityMode
  use_cache?: boolean
}

const hookName = 'useAvailabilities'
const log = logFactory(hookName)

const selectFirstAvailability = ({
  cache,
  current,
  dayKey,
  next,
}: {
  cache: DayAvailability
  current?: FirstAvailability
  dayKey?: string
  next?: FirstAvailability
}): FirstAvailability | undefined => {
  if (!cache || !dayKey) {
    return current
  }

  switch (true) {
    // If current is empty and next is not, return next
    case !current && Boolean(next):
      return next
    // If next is less than current, return next
    case current && next && isBefore(next.start_date, current.start_date):
      return next
    // If current is not part of the latest cache, return next
    case Boolean(current): {
      // @ts-expect-error Thinks current can be undefined even though we are guarding against it
      const isCurrentStillValid = cache?.[dayKey]?.[current.resource_id]?.some(({ date_start }) => isEqual(date_start, current.start_date))

      if (isCurrentStillValid) {
        return current
      }

      return next
    }
    default:
      return undefined
  }
}

const useAvailabilities = ({
  appointmentType: appointment_type,
  country,
  dateEnd: date_end,
  dateStart: date_start,
  durationInMinutes: duration_in_minutes,
  isCabinOnlyConfiguration,
  isEnabled,
  journey,
  paramsSettled = false,
  resources,
  resourceIds: resource_ids,
  timeZone: customer_timezone,
  mode,
}: Props) => {
  const { getValues, setValue, watch } = useFormContext<AppointmentFormData>()
  const [mergedAvailabilitiesResponses, setMergedAvailabilitiesResponses] = useState<AvailabilitiesResponse>()
  const cache = useRef<Record<string, AvailabilitiesResponse>>({})
  const { getAvailabilities, isReady } = useAppointmentsService()
  const { setStatus: setSchedulingStatus, status: schedulingStatus } = useSchedulingState()
  const { dateToISODateKey } = useDateFormat()
  const { setFirstAvailability } = useFirstAvailability()
  const shouldUseCache = useAvailabilityCacheEnabled()
  const [specialistId] = watch(['specialist_id'])

  const params: GetAvailabilitiesParameters = {
    appointment_type,
    customer_timezone,
    duration_in_minutes,
    date_end,
    date_start,
    journey,
    resource_gids: resources.map((resource) => resource.gid),
    mode,
  }

  const verboseAvailabilityCountries = [Country.CH]
  const isVerboseAvailabilityEnabled = verboseAvailabilityCountries.includes(country)

  if (isVerboseAvailabilityEnabled) {
    params.is_verbose = true
  }

  if (shouldUseCache) {
    params.use_cache = true
  }

  const fetchAvailabilities = async () => {
    log(`📆 Fetching resource availabilities...`, params)

    const found = await getAvailabilities(params)

    log(`📆 Found resource availabilities`, found)

    return found
  }

  const isQueryEnabled = Boolean(isReady && isEnabled)
  const results = useQuery([hookName, params], fetchAvailabilities, { enabled: isQueryEnabled })
  const { checkAvailabilitiesRequestRetry } = useAvailabilitiesRetry({ results })

  useEffect(() => {
    checkAvailabilitiesRequestRetry()
  }, [results])

  const { isLoading, error } = results
  const rawData = results?.data
  const errorCode = (error as any)?.response?.data.error_code ?? ''

  // Build per-shop CABIN_ONLY map for consultant (single shop): when cabin-only, pass { [shopId]: true }
  const shopIdToIsCabinOnly = useMemo(() => {
    if (!isCabinOnlyConfiguration || !resources?.length) return undefined
    const shopId = resources[0].shop_id // All resources are from the same shop in consultant (single shop)
    return shopId ? { [shopId]: true } : undefined
  }, [isCabinOnlyConfiguration, resources])

  const filteredData = useMemo(
    () => filterSlotsByResourceTypes(rawData, resources, shopIdToIsCabinOnly),
    [rawData, resources, shopIdToIsCabinOnly]
  )
  const data = useMemo(() => mergeAvailabilities(filteredData), [filteredData])

  // get first slot to compute firstAvailability
  const sortedDays = Object.keys(data).sort()
  const firstSlot = data[sortedDays[0]]?.[0]
  const hasSpecialistSelection = Boolean(specialistId)

  const { date_start: specialistStartDate, resource_ids: specialistResourceIds } = hasSpecialistSelection
    ? getSpecialistFirstAvailability({
        availabilities: data,
        specialistId,
      })
    : { date_start: null, resource_ids: null }

  const computedFirstAvailability: FirstAvailability | undefined = useMemo(() => {
    // if there's a specialist selected, find the first availability of that specialist
    if (specialistStartDate && specialistResourceIds) {
      return {
        start_date: new Date(specialistStartDate),
        resource_ids: specialistResourceIds.split(','),
      }
    }
    // Use computed first slot if available, otherwise fall back to API's firstAvailability
    if (firstSlot) {
      return { start_date: firstSlot.date_start, resource_ids: firstSlot.resource_ids }
    }
    // Fallback to API's firstAvailability (convert resource_id to resource_ids if needed)
    const apiFirstAvailability = rawData?.firstAvailability
    if (apiFirstAvailability) {
      const apiResourceIds = apiFirstAvailability.resource_ids ?? [(apiFirstAvailability as any).resource_id]
      return { start_date: apiFirstAvailability.start_date, resource_ids: apiResourceIds }
    }
    return undefined
    // Use primitive values as deps to prevent recomputation when rawData/firstSlot
    // get new object references but contain the same availability data.
  }, [
    firstSlot?.date_start?.getTime(),
    JSON.stringify(firstSlot?.resource_ids),
    rawData?.firstAvailability?.start_date?.getTime(),
    JSON.stringify(rawData?.firstAvailability?.resource_ids),
    specialistStartDate,
    specialistResourceIds,
  ])

  const cacheKey = `${appointment_type}:${customer_timezone}:${duration_in_minutes}:${(resource_ids ?? []).join('_')}`
  const isValidCacheKey = Boolean(appointment_type && customer_timezone && duration_in_minutes && resource_ids && resource_ids.length)

  if (isValidCacheKey && filteredData) {
    const merged = mergeDayAvailability({
      dateEndKey: dateToISODateKey(params.date_end),
      dateStartKey: dateToISODateKey(params.date_start),
      fresh: filteredData.dayAvailability,
      stale: cache.current?.[cacheKey]?.dayAvailability,
    })

    const dayAvailability = appendAllResourcesDayAvailability(merged, resources)

    const resourceHasCalendarIntegration = {
      ...cache.current?.[cacheKey]?.resourceHasCalendarIntegration,
      ...filteredData.resourceHasCalendarIntegration,
    }

    const hasAppointmentTypeMappingError = errorCode === ErrorCode.SYCLE_PRO_SHOP_APPOINTMENT_MAPPING_TYPE_NOT_FOUND

    cache.current = {
      ...cache.current,
      [cacheKey]: {
        dayAvailability,
        responseStatus: filteredData.responseStatus || 500,
        firstAvailability: computedFirstAvailability,
        resourceHasCalendarIntegration: {
          ...resourceHasCalendarIntegration,
          [ANY_RESOURCE_ID]: Object.values(resourceHasCalendarIntegration).some(Boolean),
          [ANY_INTERNAL_RESOURCE_ID]: resources.filter(filterByInternalResource).some((r) => resourceHasCalendarIntegration[r.id]),
          [ANY_EXTERNAL_RESOURCE_ID]: resources.filter(filterByExternalResource).some((r) => resourceHasCalendarIntegration[r.id]),
        },
        hasAppointmentTypeMappingError,
      },
    }

    const cacheData = cache.current[cacheKey]

    cache.current[cacheKey].firstAvailability = selectFirstAvailability({
      cache: cacheData.dayAvailability,
      current: cacheData.firstAvailability,
      dayKey: cacheData.firstAvailability ? dateToISODateKey(cacheData.firstAvailability.start_date) : undefined,
      next: computedFirstAvailability,
    })
  }

  const firstAvailability = isValidCacheKey ? cache.current[cacheKey]?.firstAvailability : undefined

  const filteredResources = useMemo(() => {
    const resourceToHasIntegration = rawData?.resourceHasCalendarIntegration
    return (resources ?? []).filter(filterResource({ isCabinOnlyConfiguration, resourceHasCalendarIntegration: resourceToHasIntegration }))
  }, [resources, rawData, isCabinOnlyConfiguration])

  const initialSpecialistId = (() => {
    if (!filteredResources?.length || hasSpecialistSelection) {
      return null
    }
    if (filteredResources.length === 1) {
      return filteredResources[0].id
    }
    return ANY_RESOURCE_ID
  })()

  useEffect(() => {
    // This is where we set the form's first availability
    if (initialSpecialistId) {
      setValue('specialist_id', initialSpecialistId)
    }
  }, [initialSpecialistId, setValue])

  // Only set the initial date from firstAvailability when no date is currently selected.
  // This prevents overwriting user's date selection on refetches or re-renders.
  // Config change handlers (appointment type, timezone) reset clientStartDate to null,
  // which allows this effect to fire again for the new configuration.
  useEffect(() => {
    if (!firstAvailability) {
      return
    }
    const currentDate = getValues('clientStartDate')
    if (currentDate) {
      return
    }

    setValue('clientStartDate', utcToZonedTime(firstAvailability.start_date, customer_timezone as string))
    setValue('clientStartTime', utcToZonedTime(firstAvailability.start_date, customer_timezone as string))
    setValue('resource_ids', firstAvailability.resource_ids ?? [])

    setFirstAvailability(firstAvailability)
  }, [customer_timezone, firstAvailability, getValues, setFirstAvailability, setValue])

  useEffect(() => {
    if ((isLoading && !rawData) || !isQueryEnabled) return

    setMergedAvailabilitiesResponses(cache.current?.[cacheKey])
  }, [cacheKey, rawData, isLoading, isQueryEnabled])

  useEffect(() => {
    if (!isValidCacheKey) {
      return
    }

    setSchedulingStatus(SchedulingStatus.FETCHING)
  }, [cacheKey, isValidCacheKey])

  useEffect(() => {
    // Transition to IDLE when we're in FETCHING and either:
    // 1. Query is enabled and has finished loading, or
    // 2. Params are settled but query is disabled due to no resources (e.g. follow-up with no resource config).
    if (schedulingStatus !== SchedulingStatus.FETCHING) {
      return
    }
    const queryFinished = isQueryEnabled && !isLoading
    const queryDisabledButSettled = paramsSettled && !isEnabled
    if (!queryFinished && !queryDisabledButSettled) {
      return
    }

    setSchedulingStatus(SchedulingStatus.IDLE)
  }, [isEnabled, isLoading, isQueryEnabled, paramsSettled, schedulingStatus])

  return {
    ...results,
    data: mergedAvailabilitiesResponses,
    hasAppointmentTypeMappingError: Boolean(mergedAvailabilitiesResponses?.hasAppointmentTypeMappingError),
  }
}

const useIsAvailabilitiesFetching = () => Boolean(useIsFetching([hookName]))

export { useAvailabilities, hookName as useAvailabilitiesHookName, useIsAvailabilitiesFetching }
